<template>
  <div class="flex flex-col gap-2">
    <MutationOverlay :mutation="setWifiMutation" />
    <div class="flex flex-col gap-1">
      {{ $t("WifiAvailableNetworksTitle") }}
      <MutationWrapper :mutation="getWifiListMutation" />
      <div
        v-if="isSuccess"
        class="flex flex-col gap-1 max-h-[30vh] overflow-auto"
      >
        <div
          class="card p-2"
          v-for="wifi in wifiList"
          :key="wifi.ssid"
          @click="ssid = wifi.ssid"
        >
          <div class="flex justify-between flex-nowrap gap-1 items-center">
            <div class="truncate">{{ wifi.ssid }}</div>
            <BleSignalIcon class="shrink-0" :rssi="wifi.rssi" :scale="1" />
          </div>

          <div v-if="wifiList?.length === 0">
            {{ $t("WifiNoAvailableNetworkText") }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div class="">
        <label class="block form-label" for="ssid">
          {{ $t("SSID") }}
        </label>
        <input
          class="form-input"
          id="ssid"
          type="text"
          v-model="ssid"
          :placeholder="$t('SSID')"
        />
      </div>

      <div class="">
        <label class="block form-label" for="password">
          {{ $t("Pass") }}
        </label>
        <klereo-password-input
          class="form-input"
          id="password"
          type="password"
          v-model="password"
          :placeholder="$t('Pass')"
        />
      </div>

      <div class="btn btn--primary" @click="submit">
        {{ $t("Connect") }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { PoolDetails } from "@/models/pool-details.model.ts";
import { useApi } from "@/services/useApi.ts";
import { onMounted, ref } from "vue";
import { useMutation } from "@tanstack/vue-query";
import MutationWrapper from "@/shared/components/MutationWrapper.vue";
import { GetWifiListResponse } from "@/models/api.models.ts";
import MutationOverlay from "@/shared/components/MutationOverlay.vue";
import { chain } from "lodash";
import BleSignalIcon from "@/features/ble/scan/BleSignalIcon.vue";
import KlereoPasswordInput from "@/shared/components/KlereoPasswordInput.vue";

const { pool } = defineProps<{
  pool: PoolDetails & { deviceId: string; localNickname: string };
}>();

const emit = defineEmits<{ close: [void: void] }>();

const wifiList = ref<GetWifiListResponse["response"] | null>();
const api = useApi();

const getWifiListMutation = useMutation({
  mutationFn: async () => {
    wifiList.value = null;
    wifiList.value = chain(await api.getWifiList())
      .orderBy((l) => l.rssi, "desc")
      .uniqBy((l) => l.ssid)
      .value();
  },
});

const { isSuccess } = getWifiListMutation;

onMounted(async () => {
  getWifiListMutation.mutate();
});

const ssid = ref<string>("");
const password = ref<string>("");

const setWifiMutation = useMutation({
  mutationFn: async () => {
    await api.setWifi({ ssid: ssid.value, password: password.value });
  },
});

const submit = async () => {
  await setWifiMutation.mutateAsync();
  emit("close");
};
</script>
